import { groq } from "next-sanity";
import { handleClientError } from "../../errors";
import { getClient } from "../sanity";

export type CdpVendor = {
  slug: string;
  name: string;
  logoUrl: string;
  lightVariantLogoUrl?: string;
};

const CDP_VENDOR_FRAGMENT =
  "{ 'slug': slug.current, name, 'logoUrl': logo.asset->url, 'lightVariantLogoUrl': lightVariantLogo.asset->url  }";

export const getCdpVendors = async (): Promise<CdpVendor[]> => {
  const query = groq`
    *[_type == "cdpVendor"] ${CDP_VENDOR_FRAGMENT}
  `;

  try {
    const cdpVendors = await getClient().fetch(query);
    return cdpVendors;
  } catch (error) {
    const message = `Failed to fetch CDP vendors: ${error instanceof Error ? error.message : String(error)}`;
    handleClientError(message);
    return [];
  }
};

export type SimpleCdpComparisonPage = Pick<CdpComparisonPage, "slug" | "firstCdpVendor" | "secondCdpVendor">;

export type CdpComparisonPage = {
  createdAt: string;
  updatedAt: string;
  slug: string;
  firstCdpVendor: CdpVendor;
  secondCdpVendor: CdpVendor;
  description: string;
  content: string;
  metaTitle?: string;
  metaDescription?: string;
  metaImageUrl?: string;
};

export const getCdpComparisonPage = async (slug: string): Promise<CdpComparisonPage | undefined> => {
  const query = groq`
    *[_type == "cdpComparison" && slug.current == $slug] {
      'createdAt': _createdAt,
      'updatedAt': _updatedAt,
      'slug': slug.current,
      firstCdpVendor->${CDP_VENDOR_FRAGMENT},
      secondCdpVendor->${CDP_VENDOR_FRAGMENT},
      description,
      content,
      metaTitle,
      metaDescription,
      'metaImageUrl': metaImage.asset->url,
    }
  `;

  try {
    const parameters = { slug };
    const cdpComparisonPage = await getClient().fetch(query, parameters);
    return cdpComparisonPage[0];
  } catch (error) {
    const message = `Failed to fetch CDP comparison page with slug "${slug}": ${
      error instanceof Error ? error.message : String(error)
    }`;
    handleClientError(message);
    return undefined;
  }
};

/** Returns a simple version of the CDP Comparison Pages, with pages including Hightouch ordered first. */
export const getCdpComparisonPages = async (): Promise<SimpleCdpComparisonPage[]> => {
  const query = groq`
    *[_type == "cdpComparison"] | score(slug.current match "hightouch") 
    | order(_score desc) {
      _score,
      'slug': slug.current,
      firstCdpVendor->${CDP_VENDOR_FRAGMENT},
      secondCdpVendor->${CDP_VENDOR_FRAGMENT},
    }
  `;

  try {
    const cdpComparisonPages = await getClient().fetch(query);
    return cdpComparisonPages;
  } catch (error) {
    const message = `Failed to fetch CDP comparison pages: ${error instanceof Error ? error.message : String(error)}`;
    handleClientError(message);
    return [];
  }
};
